import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from 'hooks/usePageTitle';
import { Joyride } from 'react-joyride';
import { getJoyrideScrollOffset } from 'utils/joyrideConfig';
import useGetAllAppointmentsByUserId from 'services/appointments/useGetAllAppointmentsByUserId';
import useSortData from 'hooks/useSortData';
import PageMenu from 'components/blocks/PageMenu/PageMenu';
import LoaderMin from 'components/blocks/Loader/LoaderMin';
import NoData from 'components/pages/noData/NoData';
import { TextMessagesTemplates } from 'components/blocks/TextMessagesTemplates/TextMessagesTemplates';
import AppointmentCardNew from 'components/blocks/AppointmentCardNew/AppointmentCardNew';
import Icon from 'components/blocks/Icon/Icon';
import { useAppointmentsTour } from 'hooks/useAppointmentsTour';
import './AppointmentsNew.scss';

const AppointmentsNew: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Mis Citas');

  const {
    getAllAppointmentsByUserId,
    crudeAppointmentsScheduled,
    crudeAppointmentsAnother,
    statusRes,
    loadingGetAllAppointmentsByUserId,
    allAppointmentsByUserId,
    errorGetAllAppointmentsByUserId,
  } = useGetAllAppointmentsByUserId();

  const [appointmentsScheduled, setAppointmentsScheduled] = useState<any[]>([]);
  const [appointmentsAnother, setAppointmentsAnother] = useState<any[]>([]);
  const [ascendingSelectedScheduled, setAscendingSelectedScheduled] = useState(false);
  const [ascendingSelectedAnother, setAscendingSelectedAnother] = useState(true);
  const [tab, setTab] = useState<'up' | 'past'>('up');

  const { orderDescendingData, orderAscendingData } = useSortData();

  useEffect(() => {
    getAllAppointmentsByUserId();
  }, []);

  useEffect(() => {
    if (crudeAppointmentsScheduled.length > 0 && allAppointmentsByUserId !== undefined) {
      orderByAscending('scheduled');
    }
  }, [crudeAppointmentsScheduled]);

  useEffect(() => {
    if (crudeAppointmentsAnother.length > 0 && allAppointmentsByUserId !== undefined) {
      orderByDescending('another');
    }
  }, [crudeAppointmentsAnother]);

  const orderByAscending = (type: string) => {
    if (type === 'scheduled') {
      orderAscendingData(crudeAppointmentsScheduled, setAppointmentsScheduled);
      setAscendingSelectedScheduled(false);
    } else {
      orderAscendingData(crudeAppointmentsAnother, setAppointmentsAnother);
      setAscendingSelectedAnother(false);
    }
  };

  const orderByDescending = (type: string) => {
    if (type === 'scheduled') {
      orderDescendingData(crudeAppointmentsScheduled, setAppointmentsScheduled);
      setAscendingSelectedScheduled(true);
    } else {
      orderDescendingData(crudeAppointmentsAnother, setAppointmentsAnother);
      setAscendingSelectedAnother(true);
    }
  };

  const items = tab === 'up' ? appointmentsScheduled : appointmentsAnother;
  const hasAppointments = items.length > 0;

  const { run, tourKey, steps, handleCallback, startTour } = useAppointmentsTour(hasAppointments);

  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener('solimat:startAppointmentsTour', handler);
    return () => window.removeEventListener('solimat:startAppointmentsTour', handler);
  }, [startTour]);

  const JOYRIDE_PROPS = {
    continuous: true, scrollToFirstStep: true,
    locale: { back: 'Anterior', close: 'Cerrar', last: 'Finalizar', next: 'Siguiente', nextWithProgress: 'Siguiente ({current} de {total})', open: 'Iniciar tour de ayuda', skip: 'Omitir tour' },
    options: { primaryColor: '#0F1F3D', zIndex: 10000, overlayColor: 'rgba(15,47,73,0.45)', showProgress: true, buttons: ['back','skip','close','primary'] } as any,
    styles: { beaconInner: { backgroundColor: '#F97316' }, beaconOuter: { borderColor: '#F97316', backgroundColor: 'rgba(249,115,22,0.2)' }, tooltip: { borderRadius: 8, fontSize: 14 }, tooltipTitle: { fontSize: 15, fontWeight: 700, textAlign: 'left' as const }, tooltipContent: { textAlign: 'left' as const } },
  };

  const sortBtn = (type: 'up' | 'past') => {
    const isAsc = type === 'up' ? ascendingSelectedScheduled : ascendingSelectedAnother;
    const apptType = type === 'up' ? 'scheduled' : 'another';
    return (
      <button
        className="btn btn--secondary btn--sm appts__sort"
        onClick={() => isAsc ? orderByAscending(apptType) : orderByDescending(apptType)}
      >
        <Icon name="calendar" size={15} />
        Fecha {isAsc ? 'reciente' : 'antigua'}
      </button>
    );
  };

  return (
    <>
      <Joyride key={tourKey} steps={steps} run={run} onEvent={handleCallback} {...JOYRIDE_PROPS} />
      <PageMenu
        title="Mis citas"
        subtitle="Consulta tu historial de citas"
        breadcrumbs={['Mis citas']}
      >
        <div className="btns-header-container">
          <button className="btn btn--ghost btn--sm" onClick={() => navigate('/')}>
            <Icon name="arrow-left" size={16} /> Inicio
          </button>
        </div>
      </PageMenu>

      <section className="section">
        <div className="container">
          {errorGetAllAppointmentsByUserId ? (
            <>{TextMessagesTemplates.errorResponse}</>
          ) : (
            <>
              <div className="tabs" role="tablist">
                <button
                  className={`tabs__tab${tab === 'up' ? ' is-active' : ''}`}
                  role="tab"
                  onClick={() => setTab('up')}
                >
                  Próximas ({appointmentsScheduled.length})
                </button>
                <button
                  className={`tabs__tab${tab === 'past' ? ' is-active' : ''}`}
                  role="tab"
                  onClick={() => setTab('past')}
                >
                  Pasadas ({appointmentsAnother.length})
                </button>
              </div>

              {(loadingGetAllAppointmentsByUserId || allAppointmentsByUserId === undefined) ? (
                statusRes === 204 ? (
                  <NoData message="No hay citas para mostrar." />
                ) : (
                  <LoaderMin />
                )
              ) : (
                <>
                  {items.length > 0 ? (
                    <>
                      <div className="appts__toolbar">
                        {sortBtn(tab)}
                      </div>
                      <div className="appts">
                        {items.map((appt: any) => (
                          <AppointmentCardNew
                            key={appt.id}
                            appt={appt}
                            isPast={tab === 'past'}
                            onClick={() => navigate(`/mis-citas/${appt.id}`)}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <NoData message="No hay citas para mostrar." />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default AppointmentsNew;
